import { useTranslation } from "react-i18next";

const About = () => {
  const { t } = useTranslation();

  return (
    <section id="about" className="py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Visual Element - Honeycomb Structure */}
          <div className="flex justify-center lg:order-first animate-scale-in">
            <div className="relative group">
              {/* Central Hexagon with DNA/Cell Symbol */}
              <div className="relative w-full max-w-md aspect-square flex items-center justify-center">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="hexagon-large bg-gradient-to-br from-neuro-cyan/30 to-neuro-purple/30 glass-neuro border-2 border-neuro-cyan/40 animate-honeycomb group-hover:scale-110 transition-transform duration-500 neuro-glow-cyan cursor-pointer"
                    style={{ width: "200px", height: "230px" }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="w-32 h-32 text-neuro-cyan animate-dna"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M6 3 Q12 6 18 3" strokeLinecap="round" />
                        <path
                          d="M6 7 Q12 10 18 7"
                          strokeLinecap="round"
                          className="opacity-80"
                        />
                        <path
                          d="M6 11 Q12 14 18 11"
                          strokeLinecap="round"
                          className="opacity-60"
                        />
                        <path
                          d="M6 15 Q12 18 18 15"
                          strokeLinecap="round"
                          className="opacity-40"
                        />
                        <path
                          d="M6 19 Q12 22 18 19"
                          strokeLinecap="round"
                          className="opacity-20"
                        />
                        <line
                          x1="6"
                          y1="3"
                          x2="6"
                          y2="21"
                          strokeLinecap="round"
                        />
                        <line
                          x1="18"
                          y1="3"
                          x2="18"
                          y2="21"
                          strokeLinecap="round"
                        />
                        <circle cx="6" cy="5" r="1.5" fill="currentColor" />
                        <circle cx="18" cy="9" r="1.5" fill="currentColor" />
                        <circle cx="6" cy="13" r="1.5" fill="currentColor" />
                        <circle cx="18" cy="17" r="1.5" fill="currentColor" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Surrounding Hexagons - Honeycomb Structure */}
                <div
                  className="hexagon absolute top-0 left-1/2 transform -translate-x-1/2 bg-neuro-cyan/20 border border-neuro-cyan/30 neuro-glow-cyan animate-hex-glow"
                  style={{
                    width: "80px",
                    height: "92px",
                    animationDelay: "0s",
                  }}
                ></div>
                <div
                  className="hexagon absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-neuro-azure/20 border border-neuro-azure/30 neuro-glow-azure animate-hex-glow"
                  style={{
                    width: "80px",
                    height: "92px",
                    animationDelay: "1s",
                  }}
                ></div>
                <div
                  className="hexagon absolute top-1/2 left-0 transform -translate-y-1/2 bg-neuro-purple/20 border border-neuro-purple/30 neuro-glow-purple animate-hex-glow"
                  style={{
                    width: "80px",
                    height: "92px",
                    animationDelay: "2s",
                  }}
                ></div>
                <div
                  className="hexagon absolute top-1/2 right-0 transform -translate-y-1/2 bg-neuro-cyan/20 border border-neuro-cyan/30 neuro-glow-cyan animate-hex-glow"
                  style={{
                    width: "80px",
                    height: "92px",
                    animationDelay: "1.5s",
                  }}
                ></div>
              </div>

              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-neuro-cyan to-neuro-purple rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity -z-10"></div>
            </div>
          </div>

          {/* Content */}
          <div className="animate-fade-in">
            <h2 className="text-4xl md:text-6xl font-bold mb-8 gradient-text">
              {t("about.title")}
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
              {t("about.description1")}
            </p>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-12">
              {t("about.description2")}
            </p>

            {/* Stats with Animation */}
            <div className="grid grid-cols-3 gap-8 mt-12">
              <div className="text-center group">
                <div className="glass-neuro p-6 rounded-2xl border border-neuro-cyan/20 hover:border-neuro-cyan/40 hover:shadow-xl hover:shadow-neuro-cyan/20 transition-all duration-500 hover:scale-105 neuro-glow-cyan cursor-pointer">
                  <h3 className="text-5xl font-bold gradient-text mb-2">5+</h3>
                  <p className="text-gray-400 text-sm font-medium">
                    {t("about.stats.experience")}
                  </p>
                </div>
              </div>
              <div className="text-center group">
                <div className="glass-neuro p-6 rounded-2xl border border-neuro-purple/20 hover:border-neuro-purple/40 hover:shadow-xl hover:shadow-neuro-purple/20 transition-all duration-500 hover:scale-105 animation-delay-100 neuro-glow-purple cursor-pointer">
                  <h3 className="text-5xl font-bold gradient-text mb-2">
                    100+
                  </h3>
                  <p className="text-gray-400 text-sm font-medium">
                    {t("about.stats.projects")}
                  </p>
                </div>
              </div>
              <div className="text-center group">
                <div className="glass-neuro p-6 rounded-2xl border border-neuro-azure/20 hover:border-neuro-azure/40 hover:shadow-xl hover:shadow-neuro-azure/20 transition-all duration-500 hover:scale-105 animation-delay-200 neuro-glow-azure cursor-pointer">
                  <h3 className="text-5xl font-bold gradient-text mb-2">50+</h3>
                  <p className="text-gray-400 text-sm font-medium">
                    {t("about.stats.clients")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
